import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router';
import { storageSize } from '@wr/shared';
import { useToast } from '../../components/Toast.js';
import { Checkbox, Dialog, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import {
  IPHONE_REMINDER,
  askPersistent,
  isInstalledApp,
  isIos,
  keepItem,
  keptItems,
  offlineState,
  offlineVersion,
  spaceProblem,
  storageInfo,
  type KeptItem,
} from '../../offline/media.js';
import { useOnline } from '../../offline/network.js';
import { useSignedIn } from '../../session.js';
import type { Asset } from './ContentScreen.js';

interface ContentContext {
  assets: Asset[] | null;
  reload(): void;
}

/**
 * Make available offline (round 2): the ticked items, each with its size;
 * a video without an offline copy is flagged and cannot be ticked. The
 * total and the free space show before anything is downloaded; nothing is
 * downloaded again later without the person asking (roaming).
 */
export function OfflineWindow() {
  const { assets, reload } = useOutletContext<ContentContext>();
  const ids = (useLocation().state as { ids?: string[] } | null)?.ids ?? [];
  const { user } = useSignedIn();
  const { store } = useOffline();
  const online = useOnline();
  const navigate = useNavigate();
  const toast = useToast();
  const close = () => navigate('/content', { replace: true });
  const [kept, setKept] = useState<Record<string, KeptItem> | null>(null);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [free, setFree] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number; current: string } | null>(
    null,
  );
  const [failed, setFailed] = useState<string[]>([]);

  const idsKey = ids.join(',');
  const items = useMemo(
    () => (assets ?? []).filter((a) => idsKey.split(',').includes(a.id)),
    [assets, idsKey],
  );

  useEffect(() => {
    if (!store) return;
    // A reading that ends once the window is closed changes nothing.
    let current = true;
    void keptItems(store).then((k) => {
      if (!current) return;
      setKept(k);
      // Ticked: what can be kept and is not up to date on this device yet.
      setChosen(
        items.filter((a) => ['none', 'update'].includes(offlineState(a, k))).map((a) => a.id),
      );
    });
    void storageInfo().then(({ usage, quota }) => {
      if (current) setFree(usage !== null && quota !== null ? Math.max(0, quota - usage) : null);
    });
    return () => {
      current = false;
    };
  }, [store, items]);

  const sizeOf = (a: Asset) => offlineVersion(a)?.size ?? 0;
  const total = (chosen ?? [])
    .map((id) => items.find((a) => a.id === id))
    .reduce((n, a) => n + (a ? sizeOf(a) : 0), 0);

  async function download() {
    if (!store || !chosen || chosen.length === 0) return;
    const space = await spaceProblem(total);
    if (space) return setProblem(space);
    setProblem(null);
    // Asked before the first download: kept content is not cleared when space runs low.
    const persistent = await askPersistent();
    const todo = items.filter((a) => chosen.includes(a.id));
    const bad: string[] = [];
    let done = 0;
    for (const a of todo) {
      setProgress({ done, total, current: a.title });
      try {
        await keepItem(store, user.id, a, (n) =>
          setProgress({ done: done + n, total, current: a.title }),
        );
      } catch {
        bad.push(a.id);
      }
      done += sizeOf(a);
    }
    setProgress(null);
    setFailed(bad);
    if (bad.length > 0) return setKept(await keptItems(store));
    toast(
      persistent
        ? `Ready offline: ${todo.length} item${todo.length === 1 ? '' : 's'} on this device.`
        : `Ready offline: ${todo.length} item${todo.length === 1 ? '' : 's'}. The browser did not promise to keep them: see Offline content in My account.`,
    );
    reload();
    close();
  }

  const busy = progress !== null;
  const pct = progress ? Math.round((progress.done / Math.max(1, progress.total)) * 100) : 0;
  return (
    <Dialog
      title="Make available offline"
      onClose={busy ? () => undefined : close}
      footer={
        <>
          <button type="button" className="btn" disabled={busy} onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !online || !chosen || chosen.length === 0 || problem !== null}
            onClick={() => void download()}
          >
            {chosen && chosen.length > 0
              ? `Download ${chosen.length} (${storageSize(total)})`
              : 'Download'}
          </button>
        </>
      }
    >
      <p className="dlg-text">
        Kept on this device for shows without network. Downloaded now only: never again without you
        asking.
      </p>
      {isIos() && !isInstalledApp() && <Notice kind="warn">{IPHONE_REMINDER}</Notice>}
      {!online && <Notice kind="warn">No network: downloading needs it.</Notice>}
      <div className="offline-pick">
        {items.map((a) => {
          const state = offlineState(a, kept ?? {});
          const why =
            state === 'unavailable'
              ? 'No offline copy: it plays only with network'
              : state === 'ready'
                ? 'Already on this device'
                : state === 'update'
                  ? `Update available, ${storageSize(sizeOf(a))}`
                  : storageSize(sizeOf(a));
          return (
            <Checkbox
              key={a.id}
              disabled={busy || state === 'unavailable' || state === 'ready'}
              checked={chosen?.includes(a.id) ?? false}
              onChange={(e) =>
                setChosen((c) =>
                  e.target.checked ? [...(c ?? []), a.id] : (c ?? []).filter((id) => id !== a.id),
                )
              }
            >
              <b>{a.title}</b>{' '}
              <span className={`note${state === 'unavailable' ? ' offline-none' : ''}`}>{why}</span>
              {failed.includes(a.id) && <span className="err"> Not downloaded: try again.</span>}
            </Checkbox>
          );
        })}
      </div>
      <p className="note">
        Total {storageSize(total)}
        {free !== null ? `, ${storageSize(free)} free on this device` : ''}.
      </p>
      {problem && <Notice kind="error">{problem}</Notice>}
      {progress && (
        <div className="uprow up" aria-live="polite">
          <div className="nm">
            <span>{progress.current}</span>
            <span>
              {storageSize(progress.done)} of {storageSize(progress.total)} ({pct}%)
            </span>
          </div>
          <div className="bar" role="progressbar" aria-label="Downloaded" aria-valuenow={pct}>
            <i style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </Dialog>
  );
}
